import { useEffect, useState } from "react";
import { voiceLine, type VoiceState } from "./voiceLine";

/**
 * The voice line on a phone: join it if you're playing from somewhere else
 * (or to be the room's microphone in the middle of the table), mute, leave.
 */
export function VoicePanel({ onJoin, onLeave, compact = false }: { onJoin: () => void; onLeave: () => void; compact?: boolean }) {
  const [s, setS] = useState<VoiceState | null>(null);
  useEffect(() => voiceLine.subscribe(setS), []);
  if (!s) return null;

  if (!s.joined) {
    if (compact) {
      return (
        <button className="text-button voice-join-link" onClick={onJoin} disabled={s.connecting}>
          {s.connecting ? "Joining the voice line…" : "Playing from elsewhere? Join the voice line"}
        </button>
      );
    }
    return (
      <section className="voice-panel" aria-label="Voice line">
        <h2 className="voice-title">The voice line</h2>
        <p className="quiet">
          Playing from somewhere else? Join to hear the room and be heard. In the room, put one phone on the line in the middle of the table so they can hear you all.
        </p>
        <button className="secondary moon-button" onClick={onJoin} disabled={s.connecting}>
          {s.connecting ? "Joining…" : "Join the voice line"}
        </button>
        {s.error && <p className="error">{s.error}</p>}
      </section>
    );
  }

  const others = s.peers;
  return (
    <section className={`voice-panel is-on ${s.hushed ? "is-hushed" : ""}`} aria-label="Voice line">
      <div className="voice-row">
        <span className={`voice-lamp ${s.hushed || s.muted ? "is-off" : ""}`} aria-hidden />
        <p className="voice-status">
          {s.hushed
            ? "The line is quiet at night."
            : others.length === 0
              ? "On the voice line. Waiting for someone else to join."
              : `On the line with ${others.map((p) => p.name + (p.state === "connected" ? "" : " (connecting)")).join(", ")}.`}
        </p>
      </div>
      <div className="voice-buttons">
        <button className={`host-btn ${s.muted ? "is-strong" : ""}`} onClick={() => voiceLine.setMuted(!s.muted)} disabled={s.hushed}>
          {s.muted ? "Unmute" : "Mute"}
        </button>
        <button className="host-btn" onClick={onLeave}>
          Leave the line
        </button>
      </div>
      {s.error && <p className="error">{s.error}</p>}
    </section>
  );
}
